/**
 * styles.css — Hestan Vineyards tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-hestanvineyards) rules override the base :host vars and the shared rich-card
 * scaffold. Moved verbatim out of variables.css (split per-winery for maintainability).
 */

/* ════════════════════════════════════════════════════════════════════════
   Hestan Vineyards — parity overrides (old hestan_vineyards/script.js +
   index.css; live hestanvineyards.com). Commerce7 BE but EXTERNAL add-to-cart
   links (no in-widget cart). Palette: bronze #8C724D (launcher, CTAs, selected
   chip), gold #8D6E35 (card titles/prices), brown #574D39 (chip text + send
   glyph), beige #E1DBD1 header, white cards w/ gold glow. Scoped to hestan; the
   shared :is(halleck,hestan,valle) scaffold values are superseded here.
   ════════════════════════════════════════════════════════════════════════ */

/* Chrome — beige header, white footer, brown send glyph, grey-gradient user bubble. */
:host(.ws-tenant-hestanvineyards) .ws-header {
  background: #e1dbd1;
}
:host(.ws-tenant-hestanvineyards) .ws-footer,
:host(.ws-tenant-hestanvineyards) .ws-input-area {
  background: #ffffff;
}
:host(.ws-tenant-hestanvineyards) .ws-btn-send {
  color: #574d39;
}
/* User bubble = grey gradient #989696→#807c7c (old .user-message). */
:host(.ws-tenant-hestanvineyards) .ws-user-bubble {
  background: linear-gradient(#989696, #807c7c);
  color: #ffffff;
}

:host(.ws-tenant-hestanvineyards) .ws-banner-img {
  height: auto;
}

/* Promo strip — old .promo-text-right: white pill, bronze text (ss1). Base
   Fogarty CTA is transparent white text (ss2); override for Hestan only.
   banner_promo_dim: false (index.js) keeps promotional_banner.png undimmed. */
:host(.ws-tenant-hestanvineyards) .ws-banner-promo {
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* Base .ws-banner-promo paints gold + 14px dots; clear so only the
     banner_bg_image from Banner.js shows (original promotional_banner.png). */
  background-color: transparent;
  background-image: none;
}

:host(.ws-tenant-hestanvineyards) .ws-banner-promo--image {
  background-color: transparent !important;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

:host(.ws-tenant-hestanvineyards) .ws-banner-cta {
  background: #ffffff;
  color: #8c724d;
  border: none;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-transform: uppercase;
  text-align: center;
  max-width: 30%;
  flex-shrink: 0;
  white-space: nowrap;
}

:host(.ws-tenant-hestanvineyards) .ws-banner-cta:hover {
  background: #ffffff;
  color: #8c724d;
  opacity: 0.92;
}

:host(.ws-tenant-hestanvineyards) .ws-banner-text {
  text-transform: uppercase;
  font-weight: 400;
  color: #ffffff;
  max-width: 62%;
}

/* Bot avatar */

:host(.ws-tenant-hestanvineyards) .ws-avatar {
  height: 28px;
  width: 28px;
}

/* Welcome chips — old .option-button: white pill, brown #574D39 text + border,
   rounded, tan #F4E3C1 hover; selected (clicked) = bronze #8C724D fill + white
   text (old changeBKG). Widget renders .ws-prompt-chip. */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-hestanvineyards) .ws-prompt-chip {
  background: #ffffff;
  color: #574d39;
  border: 1px solid #574d39;
  border-radius: 0px;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
}
:host(.ws-tenant-hestanvineyards) .ws-prompt-chip:hover {
  background: var(--ws-primary);
  color: #fff;
  border-color: var(--ws-primary);
}
:host(.ws-tenant-hestanvineyards) .ws-prompt-chip .ws-prompt-icon {
  color: inherit;
}
:host(.ws-tenant-hestanvineyards) .ws-prompt-chip.is-selected {
  background: #8c724d !important;
  border-color: #8c724d !important;
  color: #ffffff !important;
  font-weight: 600;
}
:host(.ws-tenant-hestanvineyards) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* Card accents — gold #8D6E35 titles/prices, bronze #8C724D CTAs (old
   add-to-cart / membership+event footer buttons). The card STRUCTURE reuses the
   shared rich-card classes; these set Hestan's colours over the scaffold. */
:host(.ws-tenant-hestanvineyards) .ws-card-cta,
:host(.ws-tenant-hestanvineyards) .ws-rich-cta {
  background: #8c724d;
  color: #ffffff;
  text-transform: none;
  white-space: nowrap;
}
:host(.ws-tenant-hestanvineyards) .ws-card-cta:hover,
:host(.ws-tenant-hestanvineyards) .ws-rich-cta:hover {
  background: #75603f;
}

/* Card accent (title / price / "Learn more" border+text) = GOLD #8D6E35 (live
   Hestan), overriding the shared scaffold's #972553 magenta. The Reserve / Buy /
   Join CTA stays bronze #8C724D via the .ws-card-cta rule above. */
:host(.ws-tenant-hestanvineyards) {
  --ws-card-accent: #8d6e35;
}

/* Tasting card cover image — live Hestan uses aspect-ratio 4/3 (taller), not the
   base 16/9 crop. (old .tasting-cover-image img: width:100%; aspect-ratio:4/3.) */
:host(.ws-tenant-hestanvineyards) .ws-tasting-card .ws-card-img {
  aspect-ratio: 4 / 3;
}

/* HESTAN wine card — live createWineListing: white card (gold glow + tan edge),
   gold #8D6E35 title + price, FULL-WIDTH "Tasting notes" pill (bronze #8C724D
   border, gold #8D6E35 text), footer ROW — price block (gold price over black
   qty) LEFT + bronze "Buy Now" RIGHT — NOT a full-width CTA stacked under the
   price (the shared scaffold stacks it; these supersede it). */
:host(.ws-tenant-hestanvineyards) .ws-rich-winecard {
  border: 1px solid #cbb389;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(100, 70, 25, 0.28);
}
:host(.ws-tenant-hestanvineyards) .ws-rich-winetitle {
  color: #8d6e35;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-tasting {
  align-self: stretch;
  justify-content: center;
  padding: 8px 12px;
  border-color: #8c724d;
  border-radius: 20px;
  color: #8d6e35;
  font-size: 14px;
  font-weight: 600;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-tasting:hover {
  border-color: #8c724d;
  color: #75603f;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-footer {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-pricerow {
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: 100%;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-pricerow .ws-card-price {
  color: #8d6e35;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-pricerow .ws-card-unit {
  color: #000000;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-cta {
  width: 100%;
  padding: 9px;
  background: #8c724d;
  border: 1px solid #8c724d;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-cta:hover {
  background: #75603f;
}

/* Wine card is 240px wide in the live (old .card width:240px) — the shared
   scaffold makes it 200px, leaving it small in the 240px carousel slot. */
:host(.ws-tenant-hestanvineyards) .ws-rich-winecard {
  width: 240px;
}

/* ── HESTAN membership card — live createWineMembership is a WHITE card (gold
   glow) with GOLD #8D6E35 title/price, bronze #8C724D learn-more border +
   join-club CTA, dark bullets — NOT the shared scaffold's burgundy/pink. */
:host(.ws-tenant-hestanvineyards) .ws-rich-membership {
  background: #ffffff;
  border: 1px solid #cbb389;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(100, 70, 25, 0.28);
  overflow: hidden;
  font-family: var(--ws-font);
}
:host(.ws-tenant-hestanvineyards) .ws-rich-membership .ws-card-head {
  background: #ffffff;
  padding: 6px;
}
/* base .ws-card-img forces 1/1 → letterboxes the wide HV.webp logo; natural aspect. */
:host(.ws-tenant-hestanvineyards) .ws-rich-membership .ws-card-img {
  aspect-ratio: auto;
  height: auto;
  border-radius: 6px;
}
:host(.ws-tenant-hestanvineyards) .ws-card-img.ws-rich-mem-logo {
  object-fit: contain;
  padding: 14px 19px;
  background: #ffffff;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-membership .ws-card-title {
  color: #8d6e35;
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  padding: 4px 10px;
  margin: 0;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-inner {
  background: transparent;
  padding: 0 10px 10px;
  margin: 0;
  border-radius: 0;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-inner .ws-card-price {
  color: #333333;
  font-size: 14px;
  font-weight: 400;
  margin: 0 0 6px;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-bullets li {
  font-size: 14px;
  font-weight: 400;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-check {
  color: #333333;
}
:host(.ws-tenant-hestanvineyards) .ws-card-learnmore {
  border-color: #8c724d;
  color: #8d6e35;
  box-shadow: none;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-membership-footer {
  align-items: flex-end;
  margin-top: 16px;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-mem-price {
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-mem-price .ws-card-price-amount {
  font-size: 16px;
  font-weight: 700;
  color: #8d6e35;
  margin: 0;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-mem-price .ws-card-price-unit {
  font-size: 12px;
  color: #333333;
  margin: 0;
}
:host(.ws-tenant-hestanvineyards) .ws-rich-membership-footer .ws-card-cta {
  border-radius: 2px;
  padding: 9px 14px;
}

/* HESTAN wine card image — live .card img: a NARROW (max-width 100px), TALL
   (aspect-ratio 1/3) bottle CENTERED on the card's WHITE bg. The shared rule
   stretches it to 100% width on the grey --ws-surface, giving a small bottle
   with grey side bars. Match the live: centered, white bg, no grey bars. */
:host(.ws-tenant-hestanvineyards) .ws-rich-head {
  padding: 8px 12px 0;
}

:host(.ws-tenant-hestanvineyards)
  .ws-hestan-winecard
  .ws-card-head
  .ws-card-img {
  width: auto;
  max-width: 100px;
  height: 180px;
  aspect-ratio: 1 / 3;
  object-fit: contain;
  margin: 0 auto;
  background: transparent;
}

/* Broken eCellar cover → winery logo (wider mark, not a bottle crop). */
:host(.ws-tenant-hestanvineyards)
  .ws-hestan-winecard
  .ws-card-head
  .ws-card-img.ws-hestan-cover-fallback {
  max-width: 140px;
  height: 180px;
  aspect-ratio: auto;
  width: auto;
  padding: 24px 12px;
  object-fit: contain;
}

/* Wine Pagination */

:host(.ws-tenant-hestanvineyards) .ws-wine-page-button {
  color: var(--ws-primary);
}

:host(.ws-tenant-hestanvineyards) .ws-wine-page-button.active {
  background: var(--ws-primary, #8b6b33);
  border-color: var(--ws-primary);
  color: #fff;
}

/* Review Button */
:host(.ws-tenant-hestanvineyards) .ws-rich-review-button {
  border-radius: 1px;
}

/* "Tasting notes" outlined pill (old .tasting-notes-link). */
:host(.ws-tenant-hestanvineyards) .ws-hestan-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid var(--ws-primary, #8b6b33);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-primary, #8b6b33);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
:host(.ws-tenant-hestanvineyards) .ws-hestan-tasting:hover {
  border-color: #8b6b33;
  color: #1a1a1a;
}

:host(.ws-tenant-hestanvineyards) .ws-hestan-winetitle {
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-primary);
  margin: 0;
  line-height: 1.25;
  /* max-height: 35px; */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:host(.ws-tenant-hestanvineyards) .ws-hestan-winecard-meta,
:host(.ws-tenant-hestanvineyards) .ws-hestan-winecard-desc {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 15px;
  /* font-weight: 600; */
  line-height: 120%;
  color: var(--ws-text);
}

/* Horizontal bottom row: price block left, BUY NOW right. */
:host(.ws-tenant-hestanvineyards) .ws-hestan-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

/* Tasting Notes Drawer */

:host(.ws-tenant-hestanvineyards) .ws-drawer-tab-collapse svg path,
:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-header-icon svg path {
  stroke: var(--ws-primary);
}

:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-header h2,
:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-pane h4,
:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-regular-price,
:host(.ws-tenant-hestanvineyards) .ws-drawer-fp-heading,
:host(.ws-tenant-hestanvineyards) .ws-drawer-ingredient-bullet,
:host(.ws-tenant-hestanvineyards) .ws-drawer-ingredient-amount {
  color: var(--ws-primary);
}

:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-intro,
:host(.ws-tenant-hestanvineyards) .ws-drawer-pairing-box {
  background: #f3f1eac2;
}

:host(.ws-tenant-hestanvineyards) .ws-wine-drawer-tab.active {
  background: var(--ws-primary);
  color: #fff;
  border-right: 5px solid var(--ws-primary);
}

:host(.ws-tenant-hestanvineyards) .ws-drawer-fp-card {
  border-top: 3px solid var(--ws-primary);
  background: #f3f1eac2;
}
:host(.ws-tenant-hestanvineyards) .ws-drawer-method-num {
  background: var(--ws-primary);
}
